import { useEffect, useState } from "react";
import { Loader2, Plus } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useListProjects,
  useCreateProject,
  getListProjectsQueryKey,
  type Project,
} from "@workspace/api-client-react";
import { useToast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const NO_PROJECT = "__none__";
const NEW_PROJECT = "__new__";

const PRIORITY_OPTIONS = [
  { value: "low", label: "Basse" },
  { value: "medium", label: "Moyenne" },
  { value: "high", label: "Haute" },
];

export interface CreateTaskValues {
  title: string;
  description: string;
  projectId: string;
  priority: string;
  dueDate: string;
}

export interface CreateTaskDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Pre-filled values (e.g. derived from an email). */
  initial: CreateTaskValues;
  /** Pre-loaded projects (optional — falls back to its own fetch). */
  projects?: Project[];
  busy?: boolean;
  /** Called with the final, user-edited values. */
  onSubmit: (values: CreateTaskValues) => void | Promise<void>;
}

/**
 * Lightweight "create a task" modal. Unlike the silent one-click create, this
 * lets the user review and edit the title, description, project, priority and
 * due date before the task is actually created — used by the À faire page when
 * turning an email into a task.
 */
export default function CreateTaskDialog({
  open,
  onOpenChange,
  initial,
  projects: projectsProp,
  busy = false,
  onSubmit,
}: CreateTaskDialogProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const createProject = useCreateProject();

  const projectsQuery = useListProjects(undefined, {
    query: {
      queryKey: getListProjectsQueryKey(),
      enabled: open && !projectsProp,
      staleTime: 60_000,
    },
  });
  const projects = projectsProp ?? projectsQuery.data ?? [];

  const [title, setTitle] = useState(initial.title);
  const [description, setDescription] = useState(initial.description);
  const [projectId, setProjectId] = useState(initial.projectId);
  const [newProjectName, setNewProjectName] = useState("");
  const [priority, setPriority] = useState(initial.priority || "medium");
  const [dueDate, setDueDate] = useState(initial.dueDate);
  const [submitting, setSubmitting] = useState(false);

  // Re-sync from `initial` each time the dialog (re)opens with a new mail.
  useEffect(() => {
    if (!open) return;
    setTitle(initial.title);
    setDescription(initial.description);
    setProjectId(initial.projectId);
    setNewProjectName("");
    setPriority(initial.priority || "medium");
    setDueDate(initial.dueDate);
  }, [open, initial]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;

    // Resolve the project: create it on the fly when the user chose "Nouveau
    // projet" and typed a name.
    let resolvedProjectId = projectId === NEW_PROJECT ? "" : projectId;
    if (projectId === NEW_PROJECT) {
      const name = newProjectName.trim();
      if (!name) {
        toast({ title: "Donnez un nom au nouveau projet", variant: "destructive" });
        return;
      }
      setSubmitting(true);
      try {
        const project = await createProject.mutateAsync({ data: { name } });
        resolvedProjectId = project.id;
        queryClient.invalidateQueries({ queryKey: getListProjectsQueryKey() });
      } catch (err) {
        toast({
          title: "Impossible de créer le projet",
          description: err instanceof Error ? err.message : String(err),
          variant: "destructive",
        });
        setSubmitting(false);
        return;
      }
      setSubmitting(false);
    }

    void onSubmit({
      title: title.trim(),
      description: description.trim(),
      projectId: resolvedProjectId,
      priority,
      dueDate,
    });
  };

  const isBusy = busy || submitting;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="dialog-create-task">
        <DialogHeader>
          <DialogTitle>Créer une tâche</DialogTitle>
          <DialogDescription>
            Vérifiez et complétez la tâche avant de la créer.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="create-task-title">Titre</Label>
            <Input
              id="create-task-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              autoFocus
              required
              data-testid="input-create-task-title"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="create-task-description">Description</Label>
            <Textarea
              id="create-task-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              placeholder="Notes, contexte, lien…"
              className="resize-none"
              data-testid="input-create-task-description"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="create-task-project">Projet</Label>
            <Select
              value={projectId === NEW_PROJECT ? NEW_PROJECT : projectId || NO_PROJECT}
              onValueChange={(v) =>
                setProjectId(v === NO_PROJECT ? "" : v)
              }
            >
              <SelectTrigger
                id="create-task-project"
                data-testid="select-create-task-project"
              >
                <SelectValue placeholder="Aucun projet" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_PROJECT}>Aucun projet</SelectItem>
                {projects.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name}
                  </SelectItem>
                ))}
                <SelectItem value={NEW_PROJECT}>
                  <span className="inline-flex items-center gap-1.5 text-primary">
                    <Plus className="w-3.5 h-3.5" />
                    Créer un nouveau projet…
                  </span>
                </SelectItem>
              </SelectContent>
            </Select>
            {projectId === NEW_PROJECT && (
              <Input
                value={newProjectName}
                onChange={(e) => setNewProjectName(e.target.value)}
                placeholder="Nom du nouveau projet"
                autoFocus
                className="mt-1.5"
                data-testid="input-new-project-name"
              />
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="create-task-due">Échéance</Label>
              <Input
                id="create-task-due"
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                data-testid="input-create-task-due"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="create-task-priority">Priorité</Label>
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger
                  id="create-task-priority"
                  data-testid="select-create-task-priority"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRIORITY_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={isBusy}
            >
              Annuler
            </Button>
            <Button
              type="submit"
              disabled={isBusy || !title.trim()}
              data-testid="button-confirm-create-task"
            >
              {isBusy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Créer la tâche
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
